import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Coins, ShoppingBag, ArrowLeft } from 'lucide-react';

const ProductCategoryLabels = {
  '1': 'Merchandise',
  '2': 'Supplement',
  '3': 'Accessory',
  '4': 'Voucher',
  '5': 'Other',
  'Merchandise': 'Merchandise',
  'Supplement': 'Supplement',
  'Accessory': 'Accessory',
  'Voucher': 'Voucher',
  'Other': 'Other'
};

const ProductDetail = ({ product, onBack, userCoins, setConfirmRedeem }) => {
  const [activeIndex, setActiveIndex] = useState(0);

  // Combine fallback main image and additional images
  const allImages = [product.imageUrl, ...(product.additionalImages || [])].filter(Boolean);
  if (allImages.length === 0) {
    allImages.push(''); // placeholder
  }

  const handlePrev = () => {
    if (activeIndex > 0) setActiveIndex(activeIndex - 1);
  };

  const handleNext = () => {
    if (activeIndex < allImages.length - 1) setActiveIndex(activeIndex + 1);
  };

  const canAfford = userCoins >= product.coinCost;
  const isOutOfStock = product.stockStatus === 'OutOfStock' || product.stockStatus === '2';
  const coinsNeeded = product.coinCost - userCoins;
  const hasSavings = product.mrpPrice && product.sellingPrice && product.mrpPrice > product.sellingPrice;
  const savingsAmount = hasSavings ? (product.mrpPrice - product.sellingPrice) : 0;

  return (
    <div className="flex flex-col h-full bg-[#101010] text-gray-200 overflow-hidden">
      {/* Sticky back-button header */}
      <div className="flex-shrink-0 flex items-center gap-3 px-4 py-3 bg-[#181818] border-b border-[#2b2b2b] z-10">
        <button
          onClick={onBack}
          className="p-2 bg-[#222] border border-[#2b2b2b] hover:bg-[#2b2b2b] rounded-lg text-gray-400 hover:text-white transition-all"
          aria-label="Go back to catalog"
        >
          <ArrowLeft size={18} />
        </button>
        <span className="font-bold text-sm tracking-wide text-white uppercase">Product Details</span>
      </div>

      {/* Scrollable Body */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5">
        {/* Image Carousel Container */}
        <div className="relative w-full h-[220px] bg-[#181818] border border-[#2b2b2b] rounded-2xl overflow-hidden flex items-center justify-center">
          {allImages.map((img, idx) => (
            <div
              key={idx}
              style={{
                position: 'absolute',
                inset: 0,
                transform: `translateX(${(idx - activeIndex) * 100}%)`,
                transition: 'transform 0.26s cubic-bezier(0.4, 0, 0.2, 1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '12px'
              }}
            >
              {img ? (
                <img
                  src={img}
                  alt={`${product.name} - ${idx + 1}`}
                  className="max-h-full max-w-full object-contain rounded-lg"
                />
              ) : (
                <ShoppingBag size={48} className="text-gray-750" />
              )}
            </div>
          ))}

          {/* Left Arrow */}
          {activeIndex > 0 && (
            <button
              onClick={handlePrev}
              className="absolute left-3 p-1.5 rounded-full bg-black/60 border border-gray-800 text-gray-300 hover:text-white hover:bg-black/80 transition-all cursor-pointer z-10"
            >
              <ChevronLeft size={16} />
            </button>
          )}

          {/* Right Arrow */}
          {activeIndex < allImages.length - 1 && (
            <button
              onClick={handleNext}
              className="absolute right-3 p-1.5 rounded-full bg-black/60 border border-gray-800 text-gray-300 hover:text-white hover:bg-black/80 transition-all cursor-pointer z-10"
            >
              <ChevronRight size={16} />
            </button>
          )}

          {/* Dot Indicators */}
          {allImages.length > 1 && (
            <div className="absolute bottom-3 flex gap-1.5 z-10">
              {allImages.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveIndex(idx)}
                  className={`h-1.5 rounded-full transition-all duration-200 ${idx === activeIndex ? 'w-4 bg-[#6CD800]' : 'w-1.5 bg-gray-650'
                    }`}
                  aria-label={`Go to slide ${idx + 1}`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Thumbnail Strip */}
        {allImages.length > 1 && (
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-gray-800 scrollbar-track-transparent">
            {allImages.map((img, idx) => (
              <button
                key={idx}
                onClick={() => setActiveIndex(idx)}
                className={`h-[46px] w-[46px] rounded-lg border overflow-hidden flex-shrink-0 bg-[#181818] p-0.5 flex items-center justify-center transition-all ${idx === activeIndex ? 'border-[#6CD800] ring-1 ring-[#6CD800]/30' : 'border-[#2b2b2b] hover:border-gray-650'
                  }`}
              >
                {img ? (
                  <img src={img} alt="" className="h-full w-full object-contain rounded" />
                ) : (
                  <ShoppingBag size={12} className="text-gray-650" />
                )}
              </button>
            ))}
          </div>
        )}

        {/* Product Meta Section */}
        <div className="space-y-2">
          {/* Status Badges */}
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-[10px] font-bold uppercase tracking-wider bg-lime-950/40 text-[#6CD800] border border-[#6CD800]/20 px-2 py-0.5 rounded">
              {ProductCategoryLabels[product.category] || 'Reward'}
            </span>
            {product.stockStatus === 'LimitedStock' || product.stockStatus === '3' ? (
              <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-500/10 text-amber-400 border border-amber-500/20 px-2 py-0.5 rounded">
                Limited Stock
              </span>
            ) : product.stockStatus === 'OutOfStock' || product.stockStatus === '2' ? (
              <span className="text-[10px] font-bold uppercase tracking-wider bg-red-500/10 text-red-400 border border-red-500/20 px-2 py-0.5 rounded">
                Out of Stock
              </span>
            ) : (
              <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-2 py-0.5 rounded">
                In Stock
              </span>
            )}
          </div>

          {/* Product Name */}
          <h1 className="text-2xl font-black tracking-wide text-white uppercase font-sans">
            {product.name}
          </h1>
        </div>

        {/* Pricing block card (₹) */}
        {product.sellingPrice && (
          <div className="bg-[#181818] border border-[#2b2b2b] p-4 rounded-xl space-y-1">
            <span className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">Store Price Reference</span>
            <div className="flex items-baseline gap-2.5 mt-1">
              <span className="text-2xl font-black text-white">₹{product.sellingPrice}</span>
              {product.mrpPrice && (
                <span className="text-sm text-gray-500 line-through">₹{product.mrpPrice}</span>
              )}
              {product.discountPercent && product.discountPercent > 0 && (
                <span className="text-xs font-bold text-[#6CD800] bg-[#162800] px-2 py-0.5 rounded border border-[#6CD800]/20">
                  {product.discountPercent}% OFF
                </span>
              )}
            </div>
            {hasSavings && (
              <p className="text-[11px] text-emerald-400 font-semibold mt-1">
                You save ₹{savingsAmount} compared to MRP
              </p>
            )}
          </div>
        )}

        {/* Coins block card (🪙) */}
        <div className="bg-[#181818] border border-[#2b2b2b] p-4 rounded-xl space-y-3">
          <div className="flex items-center justify-between border-b border-[#222] pb-2">
            <div className="flex flex-col">
              <span className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">Redemption Cost</span>
              <span className="text-lg font-black text-[#6CD800] mt-0.5 flex items-center gap-1.5">
                🪙 {product.coinCost.toLocaleString()} Coins
              </span>
            </div>
            <Coins className="text-lime-400" size={24} />
          </div>

          {/* Balance comparison table */}
          <div className="grid grid-cols-2 gap-4 text-xs">
            <div className="p-2.5 rounded-lg bg-[#111] border border-[#222]">
              <span className="text-gray-500 font-semibold block text-[10px] uppercase">Your Balance</span>
              <span className={`text-sm font-bold block mt-0.5 ${canAfford ? 'text-lime-400' : 'text-red-400'}`}>
                🪙 {userCoins.toLocaleString()}
              </span>
            </div>
            <div className="p-2.5 rounded-lg bg-[#111] border border-[#222]">
              <span className="text-gray-500 font-semibold block text-[10px] uppercase">Coins Required</span>
              <span className="text-sm font-bold text-white block mt-0.5">
                🪙 {product.coinCost.toLocaleString()}
              </span>
            </div>
          </div>

          {/* Bonus Offer Line */}
          {product.bonusOffer && (
            <div className="bg-[#162800] border border-[#6CD800]/20 p-3 rounded-lg flex items-start gap-2.5">
              <span className="text-xs font-bold text-[#6CD800] leading-relaxed">
                🎁 {product.bonusOffer}
              </span>
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="h-px bg-[#222]" />

        {/* About this product */}
        <div className="space-y-1.5 pb-8">
          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider">About This Product</h3>
          <p className="text-xs text-gray-300 leading-relaxed whitespace-pre-wrap">
            {product.description || 'No description available for this reward.'}
          </p>
        </div>
      </div>

      {/* Sticky CTA Strip */}
      <div className="flex-shrink-0 p-4 bg-[#181818] border-t border-[#2b2b2b] z-10">
        {isOutOfStock ? (
          <button
            disabled
            className="w-full py-3 bg-[#222] border border-[#333] text-red-500 rounded-xl text-xs font-black tracking-wider uppercase cursor-not-allowed flex items-center justify-center gap-1.5"
          >
            Out of Stock
          </button>
        ) : canAfford ? (
          <button
            onClick={() => setConfirmRedeem(product)}
            className="w-full py-3 bg-[#6CD800] text-zinc-950 rounded-xl text-xs font-black tracking-wider uppercase hover:bg-[#5bc200] active:scale-[0.99] transition-all cursor-pointer flex items-center justify-center gap-2"
          >
            Redeem with {product.coinCost.toLocaleString()} Coins
          </button>
        ) : (
          <button
            disabled
            className="w-full py-3 bg-[#222] border border-[#333] text-gray-500 rounded-xl text-xs font-black tracking-wider uppercase cursor-not-allowed flex items-center justify-center gap-1.5"
          >
            Insufficient Coins — Need {coinsNeeded.toLocaleString()} More
          </button>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;
